import { useEffect, useState } from "react";
import { Heart } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

// Shared cache so cards on the same page don't each call the API
let _favSet = null;
const _listeners = new Set();

async function loadFavs() {
  try {
    const { data } = await api.get("/me/favorites/ids");
    _favSet = new Set(data.project_ids || []);
  } catch {
    _favSet = new Set();
  }
  _listeners.forEach((cb) => cb());
}

function useFavSet(user) {
  const [, force] = useState(0);
  useEffect(() => {
    const cb = () => force((n) => n + 1);
    _listeners.add(cb);
    if (user && _favSet === null) loadFavs();
    if (!user) _favSet = null;
    return () => _listeners.delete(cb);
  }, [user]);
  return _favSet;
}

export default function FavoriteButton({ projectId, className = "", floating = false }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const favSet = useFavSet(user);
  const isFav = favSet ? favSet.has(projectId) : false;
  const [busy, setBusy] = useState(false);

  const toggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      toast.info("Sign in to save projects");
      nav("/login", { state: { from: window.location.pathname } });
      return;
    }
    if (busy) return;
    setBusy(true);
    try {
      if (isFav) {
        await api.delete(`/me/favorites/${projectId}`);
        _favSet.delete(projectId);
      } else {
        await api.post(`/me/favorites/${projectId}`);
        _favSet.add(projectId);
        toast.success("Saved to favorites");
      }
      _listeners.forEach((cb) => cb());
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const base = floating
    ? "w-9 h-9 rounded-full bg-white/95 hover:bg-white shadow-sm"
    : "w-9 h-9 rounded-full bg-white/95 hover:bg-white";

  return (
    <button
      type="button"
      onClick={toggle}
      aria-pressed={isFav}
      aria-label={isFav ? "Remove from favorites" : "Save to favorites"}
      data-testid={`favorite-btn-${projectId}`}
      className={`${base} inline-flex items-center justify-center transition-all ${className}`}
    >
      <Heart className={`w-4 h-4 ${isFav ? "fill-destructive text-destructive" : "text-forest"}`} />
    </button>
  );
}
